/**
 * Form values the "Remember these values" checkbox keeps in localStorage, so repeated
 * test deposits do not need the whole form refilled. A stored entry means the checkbox is ticked.
 */
export interface RememberedValues {
    broker?: number,
    paymentMethod?: number,
    currency?: number,
    language?: number,
    theme?: string,
    country?: number,
    source?: number,
    amount?: number,
    phone?: string,
    address?: string,
    zip?: string,
    email?: string,
    city?: string,
    firstName?: string,
    lastName?: string
}

export const REMEMBERED_VALUES_KEY = 'virtualTerminal.remembered';

const NUMBER_FIELDS = ['broker', 'paymentMethod', 'currency', 'language', 'country', 'source', 'amount'] as const;
const STRING_FIELDS = ['theme', 'phone', 'address', 'zip', 'email', 'city', 'firstName', 'lastName'] as const;

/**
 * Returns the stored values, or null when nothing is stored, storage is unavailable or the
 * entry is not valid JSON. Fields of the wrong type are dropped so the form default applies.
 */
export const loadRememberedValues = (): RememberedValues | null => {
    try {
        const raw = JSON.parse(localStorage.getItem(REMEMBERED_VALUES_KEY) ?? 'null');
        if (!raw || typeof raw !== 'object' || Array.isArray(raw))
            return null;
        const values: RememberedValues = {};
        NUMBER_FIELDS.forEach(f => {
            if (typeof raw[f] === 'number' && Number.isFinite(raw[f]))
                values[f] = raw[f];
        });
        STRING_FIELDS.forEach(f => {
            if (typeof raw[f] === 'string')
                values[f] = raw[f];
        });
        return values;
    } catch {
        return null;
    }
};

export const saveRememberedValues = (values: RememberedValues) => {
    try {
        localStorage.setItem(REMEMBERED_VALUES_KEY, JSON.stringify(values));
    } catch {
        // Storage full or disabled: the form still works, it just will not be prefilled next time.
    }
};

export const clearRememberedValues = () => {
    try {
        localStorage.removeItem(REMEMBERED_VALUES_KEY);
    } catch {
        // Storage disabled: there is nothing stored to remove.
    }
};
